import { z } from 'zod';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';
import { DEFAULT_WELCOME_TEMPLATE } from './welcome-sms.js';

/**
 * Registered SMS providers. Extend this list when a new integration ships.
 *  - `mock`    : dev / secours — loggue sans envoyer, auto-vérifie l'OTP.
 *  - `smsmode` : intégration directe historique (apiKey dans app_settings).
 *  - `gateway` : gateway messaging Hipto (`POST /v1/messages`) — credentials
 *    côté ENV serveur (jamais dans app_settings), routage/expéditeur/STOP
 *    gérés gateway-side. Cible de la migration 2026-08.
 */
export const SmsProviderNameSchema = z.enum(['mock', 'smsmode', 'gateway']);
export type SmsProviderName = z.infer<typeof SmsProviderNameSchema>;

export const SmsKindSchema = z.enum(['otp', 'text', 'welcome']);
export type SmsKind = z.infer<typeof SmsKindSchema>;

export const SmsStatusSchema = z.enum(['pending', 'sent', 'delivered', 'failed', 'verified', 'expired']);
export type SmsStatus = z.infer<typeof SmsStatusSchema>;

// ── Config persisted via AppSetting ─────────────────────────

/**
 * Runtime configuration for the SMS subsystem. Stored in `app_settings` under
 * the key `sms`. Per-provider credentials live in a sub-object so an admin
 * can prepare the next provider while another is still the "active" one.
 */
/**
 * OTP SMS body template. Placeholders remplacés au send :
 *  - `{code}` : OTP 4 chiffres
 *  - `{prenom}` : prénom du **vendeur** (commercial qui pousse le code, depuis
 *    `request.user.firstName`). Utilisé pour personnaliser le message côté
 *    prospect ("l'échange avec X").
 *  - `{privacyUrl}` : URL de la politique de confidentialité (cf. SmsConfig).
 *
 * Le template par défaut couvre la collecte du consentement RGPD : le code
 * tapé dans le wizard vaut acceptation de la politique de confidentialité,
 * et le SMS porte cette mention pour que la trace côté prospect soit explicite.
 *
 * Limite physique : 320 chars (2 segments SMS max). 1 segment GSM-7 = 160
 * chars ; au-delà, l'opérateur facture 2 segments.
 */
export const DEFAULT_OTP_TEMPLATE =
  'Ravis que l\'échange avec {prenom} se soit bien passé ! Par la communication de ce code : {code}, j\'accepte la Politique de confidentialité de Club Conso : {privacyUrl}';

export const SmsConfigSchema = z.object({
  activeProvider: SmsProviderNameSchema.default('mock'),
  /**
   * URL shown in the consent line when the template references it — kept
   * for back-compat with templates that embed `{privacyUrl}`. New default
   * template does not use it since consent is captured via checkboxes now.
   */
  privacyUrl: z.string().url().or(z.literal('')).default(''),
  /** Displayed name of the SMS sender (operator-dependent). */
  senderName: z.string().max(11).default('Doorly'),
  /** OTP SMS body template with `{code}` placeholder. */
  otpTemplate: z.string().min(1).max(320).default(DEFAULT_OTP_TEMPLATE),
  /**
   * Template du SMS de bienvenue envoyé à la livraison effective de la
   * fiche (cf. `welcome-sms.ts` pour les placeholders et le rendu). Piloté
   * séparément du flag de rollout `features.welcomeSms` : on peut ajuster
   * le texte pendant que l'envoi est encore `off`.
   */
  welcomeTemplate: z.string().min(1).max(320).default(DEFAULT_WELCOME_TEMPLATE),
  smsmode: z
    .object({
      apiKey: z.string().default(''),
      /** Optional webhook base URL for delivery receipts — ignored if empty. */
      webhookBaseUrl: z.string().url().or(z.literal('')).default(''),
    })
    .default({}),
});
export type SmsConfig = z.infer<typeof SmsConfigSchema>;

// ── App settings (generic KV) ───────────────────────────────

export const AppSettingSchema = z.object({
  key: z.string().min(1).max(100),
  value: z.unknown(),
  updatedAt: IsoDateStringSchema.optional(),
  updatedBy: UuidV7Schema.nullable().optional(),
});
export type AppSetting = z.infer<typeof AppSettingSchema>;

export const UpdateAppSettingSchema = z.object({
  value: z.unknown(),
});
export type UpdateAppSettingInput = z.infer<typeof UpdateAppSettingSchema>;

// ── SMS endpoints DTOs ──────────────────────────────────────

export const SendOtpSchema = z.object({
  phone: z.string().min(6).max(30),
  leadId: UuidV7Schema.nullable().optional(),
});
export type SendOtpInput = z.infer<typeof SendOtpSchema>;

export const SendOtpResponseSchema = z.object({
  smsLogId: UuidV7Schema,
  provider: SmsProviderNameSchema,
  expiresAt: IsoDateStringSchema,
});
export type SendOtpResponse = z.infer<typeof SendOtpResponseSchema>;

export const VerifyOtpSchema = z.object({
  smsLogId: UuidV7Schema,
  code: z.string().regex(/^\d{4}$/, 'Code à 4 chiffres attendu'),
});
export type VerifyOtpInput = z.infer<typeof VerifyOtpSchema>;

export const VerifyOtpResponseSchema = z.object({
  verified: z.boolean(),
  verifiedAt: IsoDateStringSchema.nullable(),
  /** Remaining attempts after this call. Hint to the UI. */
  attemptsLeft: z.number().int().min(0),
});
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;

// ── Admin: SMS logs listing ─────────────────────────────────

export const SmsLogItemSchema = z.object({
  id: UuidV7Schema,
  leadId: UuidV7Schema.nullable(),
  phone: z.string(),
  provider: z.string(),
  providerMessageId: z.string().nullable(),
  kind: SmsKindSchema,
  status: SmsStatusSchema,
  statusDetails: z.string().nullable(),
  sentAt: IsoDateStringSchema,
  verifiedAt: IsoDateStringSchema.nullable(),
  expiresAt: IsoDateStringSchema,
  attempts: z.number().int().min(0),
  // NB: `code` and `text` are not exposed in list responses by default —
  // they're audit-sensitive. A single-item detail endpoint could add them
  // later if needed.
});
export type SmsLogItem = z.infer<typeof SmsLogItemSchema>;

export const TestSmsSchema = z.object({
  phone: z.string().min(6).max(30),
});
export type TestSmsInput = z.infer<typeof TestSmsSchema>;

/**
 * Test du SMS de bienvenue sur une **vraie fiche** : le texte est rendu avec
 * les données du lead (vendeur, date de saisie, verticale, numéro miroir) et
 * envoyé au numéro indiqué — celui de l'admin, jamais celui du prospect.
 * Sert à valider le rendu en prod avant de basculer le flag sur `on`.
 */
export const TestWelcomeSmsSchema = z.object({
  leadId: UuidV7Schema,
  /** Destinataire du test. Obligatoire : on n'envoie JAMAIS au prospect ici. */
  phone: z.string().min(6).max(30),
});
export type TestWelcomeSmsInput = z.infer<typeof TestWelcomeSmsSchema>;

export const TestWelcomeSmsResponseSchema = z.object({
  /** Texte exactement tel qu'il partirait au prospect. */
  text: z.string(),
  encoding: z.enum(['GSM-7', 'UCS-2']),
  segments: z.number().int().min(0),
  /** Null quand le lead n'a pas de numéro miroir (2e phrase omise). */
  mirrorPhone: z.string().nullable(),
  smsLogId: UuidV7Schema,
  provider: SmsProviderNameSchema,
});
export type TestWelcomeSmsResponse = z.infer<typeof TestWelcomeSmsResponseSchema>;
